import { link as linkSteps, titleCase } from '@nsb/core';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { errorText } from '../../api.ts';
import { Exceptions } from '../process/Exceptions.tsx';
import { TickPreview } from '../process/PhaseList.tsx';
import { tickPreview } from '../process/phaseList.ts';
import { jobChoiceRefusal, jobChoices, type Side } from '../process/needsOpens.ts';
import { StepPanel } from '../process/StepPanel.tsx';
import { useStepIcons } from '../process/stepIcons.ts';
import { processAction, useTrades, type ProcessModel } from '../process/useProcess.ts';
import { unsaved } from '../../unsaved.ts';
import { Button, ErrorLine, TextInput } from '../ui.tsx';
import { jobChain, jobFindSteps, jobPhases, jobTiles, setupAction, type JobMode } from './job.ts';
import { linesOf, type TileLine } from './model.ts';
import { TOOL_KINDS, type ToolKind } from './kinds.ts';
import { OwnStepCard } from './OwnStepCard.tsx';
import type { OwnKind, OwnStepBody } from './ownStep.ts';
import { TreeTools } from './Tools.tsx';
import { TreeView } from './TreeView.tsx';

/**
 * A job's process as NSB works it (design study 12, as Jason answered it, 2026-10-06): the master's drawing, the job's
 * own way of growing on it. Its steps as tiles, where each stands at its foot (states A); a step left out a dot in the
 * line where it would have been, clicked open with + Add and the filing box; a step of the job's own dragged from the
 * tools at the top into a phase, Work, a decision, a gate or a milestone, its card open to set its title, who does it and
 * what it comes after before it is added (own step A, OwnStepCard). A click opens a step with its chain alone and its
 * panel as the card (open A); a line there clicked can be taken out. Drawn, opened and closed as every tree is
 * (TreeView). A line is drawn from a step's foot down onto what follows it. What is done here goes into the job's draft,
 * saved together with Save the changes (the Process page's bar). Nothing the job has not got is drawn: a step is added
 * from its phase's list above the drawing, or found by name (design study 31, slice 2), and a step whose phase has closed
 * comes late, into the phase under way, adding it asking for NSB's reason (LateReason), which the client confirms; on
 * NSB's own job the reason is enough. A job kept as it ended (completed or archived, L-19 A) offers nothing on either
 * page: no tools but Find, no dots, no lines drawn. Setting it up, a step's panel lists what it needs and what it opens
 * (design study 31, slice 5), + It needs… and + It opens… choosing the step on the board or by its name (TreeView's
 * `choosing`), a step it cannot join greyed with why (`jobChoiceRefusal`), the wait sent into the job's draft.
 */

/** The master's four kinds, the gate among them (study 12, own step A). */
const JOB_KINDS: ToolKind[] = TOOL_KINDS;

/** The key the card of a step of the job's own has while it is being set, before it is on the job. */
const NEW_STEP = 'new:step';

const FilingBox = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M2.5 6h11v7.5h-11z M1.8 3.5h12.4V6H1.8z M6 8.5h4" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

export function JobTree({ projectId, model, setup }: { projectId: string; model: ProcessModel; setup: boolean }) {
  const icons = useStepIcons();
  const { data: tradesDto } = useTrades();
  const trades = useMemo(() => tradesDto?.trades ?? [], [tradesDto]);
  const tradeName = useCallback((k: string) => trades.find((t) => t.key === k)?.name ?? null, [trades]);
  // Opened on a step when a link names one (?step=).
  const [focus, setFocus] = useState<string | null>(() => new URLSearchParams(window.location.search).get('step'));
  const [found, setFound] = useState<string | null>(null);
  const [arming, setArming] = useState<ToolKind | null>(null);
  // A kind dropped into a phase: its card open, the step not on the job until it is added.
  const [making, setMaking] = useState<{ kind: OwnKind; phase: string | null } | null>(null);
  const [asked, setAsked] = useState<TileLine | null>(null);
  const [reason, setReason] = useState('');
  // A step found by name and ticked: what it brings shown first, as on its phase's list (design study 31, slice 2).
  const [ticking, setTicking] = useState<string | null>(null);
  // "+ It needs…" or "+ It opens…" pressed on a step's panel: the step chosen on the board, or by its name (slice 5).
  const [choosing, setChoosing] = useState<{ key: string; side: Side } | null>(null);
  const [busy, setBusy] = useState(false);
  const [words, setWords] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Kept as it ended (L-19 A): nothing added, filed away or redrawn, on either page.
  const kept = Boolean(model.dto.kept);
  const edits = setup && !kept;
  const mode = useMemo((): JobMode => (setup ? { setup: true, kept } : { setup: false }), [setup, kept]);
  const steps = useMemo(() => jobTiles(model, icons.urls, tradeName, mode), [model, icons.urls, tradeName, mode]);
  const lines = useMemo(() => linesOf(steps), [steps]);
  const titleOf = useCallback((id: string) => model.step(id)?.title ?? null, [model]);
  const phases = useMemo(() => jobPhases(model, steps, titleOf), [model, steps, titleOf]);
  // A step drawn as a dot is named by its key in the tree.
  const title = (id: string) => titleCase(model.step(id)?.title ?? model.tree.node(id.replace(/^(next|out):/, ''))?.title ?? 'that step');

  // Opened: a step of the job at the top of the tree (a task asked for, by an exception or a link, opens its step,
  // where it is listed with its own controls).
  const topOf = (id: string): string => {
    let s = model.step(id);
    for (let i = 0; s?.parentId && model.step(s.parentId) && i < 100; i++) s = model.step(s.parentId);
    return s?.id ?? id;
  };
  // Only what is drawn: a step turned off is not opened any more.
  const wanted = focus ? (model.step(focus) ? topOf(focus) : focus) : null;
  const focused = making ? NEW_STEP : wanted && steps.some((s) => s.key === wanted) ? wanted : null;
  // A step opened: its whole chain through the job (`jobChain`).
  const chain = useMemo(() => jobChain(steps, focused), [steps, focused]);
  // Choosing for the step opened, while the job can be changed: what the typed field finds, the job's steps in its walk.
  const joining = edits && choosing && focused === choosing.key && model.step(choosing.key) ? choosing : null;
  const choices = useMemo(() => (joining ? jobChoices(model) : []), [joining, model]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      // Esc closes a step opened first (TreeView's, a new step's card among them), then a kind chosen, then what was found.
      if (e.key === 'Escape' && !focused) {
        if (arming) setArming(null);
        else setFound(null);
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [focused, arming]);

  /** A change sent, in words; it goes into the job's draft on the Process page. */
  const send = async (path: string, body: unknown, said: string): Promise<boolean> => {
    setBusy(true);
    setErr(null);
    try {
      await processAction(projectId, path, body);
      setWords(said);
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  /** Why `dependent` cannot wait on `needed`, or null: the job's own rule, asked before anything is sent. */
  const check = (dependent: string, needed: string): string | null => {
    if (dependent.startsWith('out:') || needed.startsWith('out:')) return 'Put it back first.';
    if (model.process.links.some((l) => l.kind === 'requires' && l.stepId === dependent && l.needsId === needed)) return `${title(dependent)} already waits on ${title(needed)}.`;
    try {
      linkSteps(model.process, dependent, needed, model.phases);
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  };

  // Find a step: setting it up, every step of the tree, added or put back from the list (L-18 C); else the job's own.
  /** Found by name and ticked: added at once when it needs nothing the job lacks and lands on time; else what it brings first. */
  const tick = (key: string) => {
    const p = tickPreview(model, [key]);
    if (p.adds.every((a) => a.asked) && !p.late) void send('/enable', { key }, `Added ${title(`next:${key}`)} to the job.`);
    else setTicking(key);
  };
  const findSteps = useMemo(() => jobFindSteps(model, steps, edits ? tick : null), [model, steps, edits]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Filed away from its tile (P-14): off the job, its own list with it; a dot, put back from its phase's list. */
  const fileAway = (stepId: string) =>
    void send(`/steps/${stepId}/off`, {}, `Filed ${title(stepId)} away: off this job. Tick it in its phase’s list to put it back.`);
  /** What a tile carries at its foot setting the job up, by the one rule (`setupAction`, P-14): the filing box on a step of the job. */
  const fileActions = (k: string): ReactNode => {
    if (!edits) return null;
    const a = setupAction(model, k);
    if (!a) return null;
    const file = a.file;
    return <FileAway title={title(k)} busy={busy} onFile={file ? () => fileAway(file.stepId) : undefined} blocked={a.blocked} />;
  };

  /** A step of the job's own added from its card, as one change in the draft; it is then opened where it landed. */
  const addOwn = async (body: OwnStepBody) => {
    const before = new Set(model.dto.steps.map((s) => s.id));
    setBusy(true);
    setErr(null);
    try {
      const dto = await processAction(projectId, '/steps', body);
      const made = dto.steps.find((s) => !before.has(s.id) && !s.off && s.title === body.title);
      setWords(`Added ${titleCase(body.title)}, this job’s own, to ${phases.find((p) => p.key === body.phase)?.name ?? 'the job'}.`);
      setMaking(null);
      setArming(null);
      setFocus(made?.id ?? null);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const card = (id: string): ReactNode => {
    if (id === NEW_STEP && making) {
      return <OwnStepCard key={`${making.kind}:${making.phase}`} kind={making.kind} phase={making.phase} phases={phases} steps={steps} busy={busy} onAdd={(b) => void addOwn(b)} onCancel={() => setMaking(null)} />;
    }
    return (
      <div className="p-5">
        <StepPanel
          key={id}
          projectId={projectId}
          model={model}
          stepId={id}
          setup={edits}
          onClose={() => unsaved.afterAsking(() => setFocus(null))}
          onTurnedOn={(key) => setWords(`Added ${titleCase(model.tree.node(key)?.title ?? 'it')} to the job.`)}
          // What the board draws, another step opened from the panel (a line's step, or Next step in the tree), and choosing
          // what it needs or opens on the board, away from a panel with changes not saved asked first, as leaving it is (slice 5).
          board={{
            phases,
            steps,
            lines,
            onOpen: (stepId) => unsaved.afterAsking(() => (setFocus(stepId), setAsked(null), setChoosing(null))),
            ...(edits ? { onChoose: (stepId: string, side: Side) => unsaved.afterAsking(() => (setChoosing({ key: stepId, side }), setAsked(null))) } : {}),
          }}
        />
      </div>
    );
  };
  // A line asked about: taken out, a hold (a gate or an inspection not passed) only with a reason.
  const holds = asked ? (() => { const from = model.step(asked.src); return Boolean(from && (from.kind === 'gate' || from.kind === 'inspection') && from.state !== 'done'); })() : false;
  const lineAsk = asked ? (
    <div role="dialog" aria-label="This wait" className="sticky top-2 z-30 mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[2px] border border-ink bg-paper px-4 py-2.5">
      <span>
        {title(asked.dst)} waits on {title(asked.src)}.
      </span>
      {holds ? <TextInput aria-label="Why it no longer waits" placeholder="Why it no longer waits" value={reason} onChange={(e) => setReason(e.target.value)} className="!h-10 w-72" /> : null}
      <button
        type="button"
        className="link"
        disabled={busy || (holds && !reason.trim())}
        onClick={() =>
          void send('/links', { stepId: asked.dst, needsId: asked.src, kind: 'requires', on: false, ...(holds ? { reason: reason.trim() } : {}) }, `${title(asked.dst)} no longer waits on ${title(asked.src)}.`).then((ok) => {
            if (ok) {
              setAsked(null);
              setReason('');
            }
          })
        }
      >
        Take it out
      </button>
      <button type="button" className="link text-ink-3" onClick={() => setAsked(null)}>
        Keep it
      </button>
    </div>
  ) : null;

  const sayLine =
    err || words ? (
      <div className="mb-2 flex min-h-11 flex-wrap items-center gap-x-4" aria-live="polite">
        {err ? <ErrorLine error={err} /> : <p className="text-[0.9375rem] text-ink-2">{words}</p>}
        {!err && model.dto.draft?.count ? (
          <Button tone="quiet" disabled={busy} onClick={() => void send('/draft/undo', {}, 'Taken back.')}>
            Undo
          </Button>
        ) : null}
      </div>
    ) : null;

  return (
    <div>
      {/* Away from a step whose card has changes not saved (P-29), asked first, as leaving the page is. */}
      <Exceptions model={model} onSelect={(k) => unsaved.afterAsking(() => (setFocus(k), setChoosing(null)))} />
      <TreeView
        phases={phases}
        steps={steps}
        lines={lines}
        label="The job’s steps"
        open={focused}
        titleOf={(k) => (k === NEW_STEP ? 'This job’s own step' : (model.tree.node(k.replace(/^(next|out):/, ''))?.title ?? null))}
        onOpen={(k) => {
          // Away from a new step's card, it is not added.
          setMaking(null);
          // A dot carries nothing: its step is put back from its phase's list, or Find.
          if (k?.startsWith('out:')) return;
          const go = () => {
            setFocus(k);
            setAsked(null);
            setChoosing(null);
          };
          // Away from a step whose card has changes not saved (P-29), asked first.
          if (k === focus) go();
          else unsaved.afterAsking(go);
        }}
        card={card}
        chainSteps={chain}
        whole="the whole job"
        tools={
          <TreeTools
            label={edits ? 'Add to this job' : ''}
            kinds={edits ? JOB_KINDS : []}
            arming={arming}
            onArm={setArming}
            find={{ steps: findSteps, onFind: setFound, heading: edits ? 'Steps of the tree' : 'Steps of the job' }}
          />
        }
        between={
          <>
            {sayLine}
            {lineAsk}
            {ticking ? <TickPreview key={ticking} projectId={projectId} model={model} keys={[ticking]} onDone={() => (setTicking(null), setWords(`Added ${title(`next:${ticking}`)} to the job.`))} onCancel={() => setTicking(null)} /> : null}
          </>
        }
        selected={found}
        keepEmpty
        draw={edits ? { check, onLink: (dependent, needed) => void send('/links', { stepId: dependent, needsId: needed }, `${title(dependent)} now waits on ${title(needed)}.`) } : undefined}
        onAddKind={edits ? (kind, phase) => {
          if (kind === 'task' || kind === 'decision' || kind === 'gate' || kind === 'milestone') setMaking({ kind, phase });
        } : undefined}
        arming={arming ? { kind: arming.kind, word: arming.as } : null}
        actionsFor={fileActions}
        onLine={edits && focused && model.step(focused) ? (l) => setAsked(l) : undefined}
        // Choosing what the step opened needs or opens (slice 5): a step it cannot join greyed with why (the job's rule),
        // the one pressed or named sent as a wait into the job's draft; then its panel again.
        choosing={
          joining
            ? {
                key: joining.key,
                side: joining.side,
                refusal: (k) => jobChoiceRefusal(model, joining.key, joining.side, k),
                onPick: (k) => {
                  const w = joining.side === 'needs' ? { stepId: joining.key, needsId: k } : { stepId: k, needsId: joining.key };
                  setChoosing(null);
                  void send('/links', w, `${title(w.stepId)} now waits on ${title(w.needsId)}.`);
                },
                onCancel: () => setChoosing(null),
                among: choices,
              }
            : undefined
        }
      />
    </div>
  );
}

/**
 * What a tile carries at its foot setting the job up (`setupAction`, P-14): the filing box on a step of the job, which
 * files it away off the job; held, saying why, where it cannot go.
 */
function FileAway({ title, busy, onFile, blocked }: { title: string; busy: boolean; onFile?: () => void; blocked?: string }) {
  const words = blocked ?? 'File it away: off this job';
  return (
    <>
      {onFile || blocked ? (
        <button
          type="button"
          disabled={busy || Boolean(blocked)}
          onClick={onFile}
          title={words}
          aria-label={blocked ? `${title}: ${blocked}` : `File ${title} away, off this job`}
          className="inline-flex h-[26px] items-center rounded-[2px] border border-ink bg-sheet px-1.5 hover:bg-ink hover:text-paper disabled:border-rule-strong disabled:text-ink-3 disabled:hover:bg-sheet"
        >
          <FilingBox />
        </button>
      ) : null}
    </>
  );
}
